/**
 * Custom TipTap Audio node extension.
 * Renders an <audio> element with controls inside the editor.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { mediaLayoutAttributes } from './tiptap-media-layout';

export type AudioPlayerStyle = 'full' | 'minimal';

export interface AudioAttributes {
    src: string;
    title?: string | null;
    /** EAA-1736: 'full' = native <audio controls>, 'minimal' = play button only. */
    style?: AudioPlayerStyle;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        audio: {
            /** Insert an audio block node. */
            setAudio: (attrs: AudioAttributes) => ReturnType;
        };
    }
}

// EAA-1736: raw <button data-slot="button"> below mirrors the kit's
// `<Button variant="default" size="icon">` class recipe (components/ui/button.tsx)
// so the static player's plain-HTML button (this path uses generateHTML, not React,
// so the real Button component can't render here) still picks up the playful skin's
// [data-skin='playful'] [data-slot='button'] depth styling for free. If Button's
// base/variant/size classes change, update this string to match.
const MINIMAL_AUDIO_BUTTON_CLASS =
    "tiptap-audio-minimal-btn inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 size-9";

// Lucide "Play" / "Pause" icon geometry (see lucide-react/dist/esm/icons/play.js
// and pause.js) inlined as raw SVG since this path renders plain HTML strings —
// kept visually identical to the editor's lucide-react icons.
const PLAY_ICON_SVG =
    '<svg class="tiptap-audio-icon tiptap-audio-icon--play" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>';
const PAUSE_ICON_SVG =
    '<svg class="tiptap-audio-icon tiptap-audio-icon--pause" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="14" y="4" width="4" height="16" rx="1"></rect><rect x="6" y="4" width="4" height="16" rx="1"></rect></svg>';

export const Audio = Node.create({
    name: 'audio',
    // EAA-1736: inline-level so audio can flow with text (inline layout).
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            src: { default: null },
            title: { default: null },
            // EAA-1736: shared layout attrs (data-layout / data-x / data-y) so
            // audio can wrap / sit behind / in front of text like other media.
            ...mediaLayoutAttributes(),
            style: {
                default: 'full',
                parseHTML: (element) => {
                    const value = element.getAttribute('data-style');
                    return value === 'minimal' ? 'minimal' : 'full';
                },
                renderHTML: (attributes) => {
                    if (attributes.style !== 'minimal') return {};
                    return { 'data-style': 'minimal' };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'audio[src]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        const isMinimal = node.attrs.style === 'minimal';

        if (!isMinimal) {
            return [
                'audio',
                mergeAttributes(HTMLAttributes, {
                    controls: 'true',
                    preload: 'metadata',
                    class: 'tiptap-audio',
                }),
            ];
        }

        // EAA-1736: "minimal" style — a play/pause button instead of the native
        // controls bar. The button is left empty here: prosemirror-model's
        // DOMOutputSpec creates SVG child elements via plain createElement (no
        // namespace), which browsers don't render as vectors, so the icon
        // markup is injected as real innerHTML by RichTextViewer's post-process
        // effect instead (see AUDIO_MINIMAL_BUTTON_ICONS_HTML below). The
        // editor's own AudioView renders a real React <Button> instead of this
        // static-viewer-only markup.
        //
        // HTMLAttributes here bundles src/title (audio-specific) together with
        // data-layout/data-x/data-y and any position `style` (layout-specific —
        // meant for the VISIBLE wrapper, not the audio tag we hide). Split them:
        // src/title go on <audio>, everything else goes on the outer <span> so
        // wrap/behind/front layout keeps working once <audio> is display:none.
        const { src, title, ...layoutAttrs } = HTMLAttributes;
        return [
            'span',
            mergeAttributes(layoutAttrs, { class: 'tiptap-audio-minimal' }),
            [
                'audio',
                mergeAttributes(
                    { src, title, 'data-style': 'minimal' },
                    { preload: 'metadata', class: 'tiptap-audio tiptap-audio--minimal-el' }
                ),
            ],
            [
                'button',
                {
                    type: 'button',
                    'data-slot': 'button',
                    'data-audio-toggle': 'true',
                    class: MINIMAL_AUDIO_BUTTON_CLASS,
                    'aria-label': 'Play',
                },
            ],
        ];
    },

    addCommands() {
        return {
            setAudio:
                (attrs) =>
                ({ commands }) => {
                    return commands.insertContent({ type: this.name, attrs });
                },
        };
    },
});

export const AUDIO_MINIMAL_BUTTON_ICONS_HTML = `${PLAY_ICON_SVG}${PAUSE_ICON_SVG}`;

export default Audio;
